import { useMemo } from 'react'

import type { CodeImageUsageKey } from '@brickup/shared/constants'
import type {
  ImageDerivativeResponse,
  ImageUsageGeometry,
} from '@brickup/shared/dto'
import { buildImageSource, ORIGINAL_SIZE } from '@brickup/shared/utils'
import { ImageWithFallback } from './ImageWithFallback'
import { useImageUsageGeometry } from '@/api/media'
import { getS3ImageUrl, S3_PUBLIC_BASE_URL } from '@/utils/s3-image-url'

type UsageImageProps = {
  /** The named place this image is shown. Its geometry decides the files. */
  usageKey: CodeImageUsageKey
  /** What the asset has rendered. Empty while it is still being reprocessed. */
  derivatives: ImageDerivativeResponse[]
  /** The original's coordinates, for the fallback. */
  uuid: string | null
  path: string | null
  format: string | null
  alt: string
  width?: number | string
  height?: number | string
  fillHeight?: boolean
  /** When the derivatives were last written. A re-render keeps every object
   *  key — the usage and the density are the whole name — so without this the
   *  browser answers from cache and a rotation or a crop looks like nothing
   *  happened. */
  version?: string | null
  /** `contain` shows the whole picture inside the box. For a surface that
   *  points at image coordinates — a focal point, a crop — anything cropped
   *  away makes the box's percentages stop being the picture's. */
  fit?: 'cover' | 'contain'
  /** Which rung of the legacy ladder to fall back to while the asset has
   *  rendered nothing at all. Matched to the surface: a 20px swatch asking
   *  for `md` pays for a file it draws at a tenth of its width. */
  fallbackSize?: 'sm' | 'md' | 'lg'
}

type SourceInput = Pick<
  UsageImageProps,
  | 'usageKey'
  | 'derivatives'
  | 'uuid'
  | 'path'
  | 'format'
  | 'version'
  | 'fallbackSize'
>

/**
 * Where one asset's files for one usage live, with the cache-busting query
 * already on them.
 *
 * The one resolution: `UsageImage` renders from it and `useUsageImageSrc`
 * reads its address, so a `<picture>` and a bare `<img>` of the same asset
 * cannot point at different files.
 *
 * Memoised because the srcset costs three filters and two sorts over every
 * derivative, and it renders once per card on a grid that re-renders on every
 * selection.
 */
function useUsageSource({
  usageKey,
  derivatives,
  uuid,
  path,
  format,
  version,
  fallbackSize = 'md',
}: SourceInput) {
  const { data: usages = [] } = useImageUsageGeometry()

  return useMemo(() => {
    const usage = usages.find(
      (entry: ImageUsageGeometry) => entry.key === usageKey,
    )
    const built =
      usage && derivatives.length > 0
        ? buildImageSource(S3_PUBLIC_BASE_URL, usage, derivatives)
        : null
    // What keeps a re-rendered derivative from answering out of the cache: the
    // object key holds the usage and the density and nothing that changes.
    const bust = version ? `?v=${encodeURIComponent(version)}` : ''

    if (!built) {
      // An asset that has rendered anything no longer has a ladder — it is
      // deleted at the end of each render — so a usage it holds nothing for
      // falls back to the original, the one file every asset keeps. That is
      // the window between a new usage being declared and the queue reaching
      // this asset.
      const size = derivatives.length > 0 ? ORIGINAL_SIZE : fallbackSize
      return {
        sources: null,
        fallbackUrl:
          uuid && path && format
            ? `${getS3ImageUrl({ uuid, path, format, size })}${bust}`
            : null,
      }
    }

    return {
      sources: {
        ...built,
        // On every candidate: the browser picks from the srcset.
        sources: built.sources.map(entry => ({
          ...entry,
          srcSet: bust ? withVersion(entry.srcSet, bust) : entry.srcSet,
        })),
        fallback: `${built.fallback}${bust}`,
      },
      fallbackUrl: null,
    }
  }, [usages, usageKey, derivatives, uuid, path, format, version, fallbackSize])
}

/**
 * The one URL of an image for a usage.
 *
 * For a surface that needs the address rather than the element — a crop
 * preview drawn with `object-position` cannot go through a `<picture>`.
 */
export function useUsageImageSrc(input: SourceInput): string | null {
  const { sources, fallbackUrl } = useUsageSource(input)
  return sources?.fallback ?? fallbackUrl
}

/**
 * An image rendered for one named usage.
 *
 * Emits a `<picture>` whose `srcset` comes from the derivatives the pipeline
 * actually wrote, so the widths it advertises are the widths of the files
 * behind them — a hand-written srcset is where those two drift and the
 * browser picks a file that is not the size it was told.
 *
 * Falls back to the old size scheme where an asset has rendered nothing yet,
 * and to the original where it has rendered other usages but not this one.
 * Neither is a defect state: a library is reprocessed in the background, and
 * both kinds of asset exist side by side while it runs.
 */
export function UsageImage({
  usageKey,
  derivatives,
  uuid,
  path,
  format,
  alt,
  width = '100%',
  height,
  fillHeight,
  version,
  fit = 'cover',
  fallbackSize = 'md',
}: UsageImageProps) {
  const { sources, fallbackUrl } = useUsageSource({
    usageKey,
    derivatives,
    uuid,
    path,
    format,
    version,
    fallbackSize,
  })

  if (!sources) {
    return (
      <ImageWithFallback
        url={fallbackUrl ?? ''}
        alt={alt}
        width={width}
        height={height}
        fillHeight={fillHeight}
      />
    )
  }

  // The usage's target width is what the srcset was built for; a caller that
  // draws it smaller says so, or the browser picks a candidate for a box four
  // times the one on screen.
  const sizes =
    typeof width === 'number' ? `${Math.round(width)}px` : sources.sizes

  return (
    <picture>
      {sources.sources.map(entry => (
        <source
          key={entry.mimeType}
          type={entry.mimeType}
          srcSet={entry.srcSet}
          sizes={sizes}
        />
      ))}
      <img
        src={sources.fallback}
        alt={alt}
        width={sources.width}
        height={sources.height ?? undefined}
        loading="lazy"
        decoding="async"
        style={{
          width: typeof width === 'number' ? `${width}px` : width,
          height:
            fillHeight || height === undefined
              ? '100%'
              : typeof height === 'number'
                ? `${height}px`
                : height,
          objectFit: fit,
        }}
      />
    </picture>
  )
}

/** The same query on every candidate of a `srcset`, whose entries are
 *  `url descriptor` pairs separated by commas. */
function withVersion(srcSet: string, bust: string): string {
  return srcSet
    .split(',')
    .map(entry => {
      const [url, ...rest] = entry.trim().split(/\s+/)
      return [`${url}${bust}`, ...rest].join(' ')
    })
    .join(', ')
}
